import React, { useState, useEffect, useContext, useRef } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPray, 
    faArrowLeft,
    faPlus,
    faHeart,
    faCheckCircle,
    faSpinner,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import { Tab, Tabs, Form, Card, Badge, Spinner, Alert, Modal, Button } from 'react-bootstrap';
import Header from './header';
import Footer from './footer';
import { timeAgo } from '../utils/functions';
import { useCustomAlert } from './CustomAlert';

const GroupPrayerRequests = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();

    // States for prayer request form
    const [prayerText, setPrayerText] = useState('');
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [charCount, setCharCount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // States for prayer lists
    const [groupPrayers, setGroupPrayers] = useState([]);
    const [myPrayers, setMyPrayers] = useState([]);
    const [testimonies, setTestimonies] = useState([]);
    const [loadingPrayers, setLoadingPrayers] = useState(true);
    const [loadingMyPrayers, setLoadingMyPrayers] = useState(true);
    const [loadingTestimonies, setLoadingTestimonies] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // State for testimony form
    const [showTestimonyForm, setShowTestimonyForm] = useState(null);
    const [testimonyText, setTestimonyText] = useState('');
    const [submittingTestimony, setSubmittingTestimony] = useState(false);

    // State for prayer form modal
    const [showPrayerForm, setShowPrayerForm] = useState(false);

    // State for encouragement modal
    const [showEncouragementModal, setShowEncouragementModal] = useState(false);
    const [selectedPrayerId, setSelectedPrayerId] = useState(null);
    const [encouragementText, setEncouragementText] = useState('');
    const [submittingEncouragement, setSubmittingEncouragement] = useState(false);

    // Polling for real-time updates
    const pollingIntervalRef = useRef(null);

    // Start polling for real-time updates
    const startPolling = () => {
        // Clear any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        
        // Poll every 30 seconds for new prayer data
        pollingIntervalRef.current = setInterval(() => {
            if (user && user.user_id && groupId) {
                checkForUpdates();
            }
        }, 30000); // 30 seconds
    };

    // Check for updates without refreshing UI unless changes are detected
    const checkForUpdates = async () => {
        try {
            // Fetch data in background without updating loading states
            const [groupPrayersResponse, myPrayersResponse, testimoniesResponse] = await Promise.all([
                fetch(`${backendUrl}/get_group_prayer_requests_unified.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        group_id: groupId,
                        user_id: user.user_id,
                        limit: 50
                    })
                }),
                fetch(`${backendUrl}/my_prayers.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: user.user_id,
                        limit: 50
                    })
                }),
                fetch(`${backendUrl}/fetch_testimonies.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: user.user_id,
                        group_id: groupId,
                        limit: 20
                    })
                })
            ]);

            const [groupPrayersData, myPrayersData, testimoniesData] = await Promise.all([
                groupPrayersResponse.json(),
                myPrayersResponse.json(),
                testimoniesResponse.json()
            ]);

            let hasChanges = false;

            // Check group prayers for changes
            if (groupPrayersData.success) {
                const newGroupPrayers = (groupPrayersData.data.prayers || []).map(prayer => ({
                    ...prayer,
                    prayer_count: parseInt(prayer.prayer_count) || 0,
                    user_prayed: parseInt(prayer.user_prayed) || 0
                }));

                // Compare with current state
                if (JSON.stringify(newGroupPrayers) !== JSON.stringify(groupPrayers)) {
                    setGroupPrayers(newGroupPrayers);
                    hasChanges = true;
                }
            }

            // Check my prayers for changes
            if (myPrayersData.success) {
                const newMyPrayers = myPrayersData.prayers || [];
                if (JSON.stringify(newMyPrayers) !== JSON.stringify(myPrayers)) {
                    setMyPrayers(newMyPrayers);
                    hasChanges = true;
                }
            }

            // Check testimonies for changes
            if (testimoniesData.success) {
                const newTestimonies = (testimoniesData.prayers || []).map(prayer => ({
                    ...prayer,
                    prayer_count: parseInt(prayer.prayer_count) || 0,
                    has_prayed: parseInt(prayer.has_prayed) || 0
                }));

                if (JSON.stringify(newTestimonies) !== JSON.stringify(testimonies)) {
                    setTestimonies(newTestimonies);
                    hasChanges = true;
                }
            }

            // Only log if there were actual changes
            if (hasChanges) {
                console.log('Prayer data updated - changes detected');
            }

        } catch (err) {
            console.error('Error checking for updates:', err);
        }
    };

    // Stop polling
    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    useEffect(() => {
        if (user && user.user_id && groupId) {
            fetchGroupPrayers();
            fetchMyPrayers();
            fetchTestimonies();
            startPolling();
        }
        
        // Cleanup polling on unmount
        return () => {
            stopPolling();
        };
    }, [user, groupId]);

    useEffect(() => {
        setCharCount(prayerText.length);
    }, [prayerText]);

    const fetchGroupPrayers = async () => {
        try {
            setLoadingPrayers(true);
            setLoadError(null);

            const response = await fetch(`${backendUrl}/get_group_prayer_requests_unified.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    limit: 50
                })
            });

            const data = await response.json();
            console.log('Fetched group prayers:', data); // Debug logging

            if (data.success) {
                // Convert string values to numbers for proper comparison
                const prayers = (data.data.prayers || []).map(prayer => ({
                    ...prayer,
                    prayer_count: parseInt(prayer.prayer_count) || 0,
                    user_prayed: parseInt(prayer.user_prayed) || 0
                }));
                setGroupPrayers(prayers);
            } else {
                setLoadError(data.message || 'Failed to load prayer requests');
            }
        } catch (err) {
            console.error('Error loading group prayers:', err);
            setLoadError('Network error. Please try again later.');
        } finally {
            setLoadingPrayers(false);
        }
    };

    const fetchMyPrayers = async () => {
        try {
            setLoadingMyPrayers(true);

            const response = await fetch(`${backendUrl}/my_prayers.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    group_id: groupId,
                    limit: 50
                })
            });

            const data = await response.json();

            if (data.success) {
                // Filter to only show prayers for this specific group
                const groupPrayers = (data.prayers || []).filter(prayer => 
                    prayer.group_id && parseInt(prayer.group_id) === parseInt(groupId)
                );
                setMyPrayers(groupPrayers);
            }
        } catch (err) {
            console.error('Error loading my prayers:', err);
        } finally {
            setLoadingMyPrayers(false);
        }
    };

    const fetchTestimonies = async () => {
        try {
            setLoadingTestimonies(true);

            const response = await fetch(`${backendUrl}/fetch_testimonies.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    group_id: groupId,
                    limit: 20
                })
            });

            const data = await response.json();

            if (data.success) {
                // Convert string values to numbers for proper comparison
                const testimonies = (data.prayers || []).map(prayer => ({
                    ...prayer,
                    prayer_count: parseInt(prayer.prayer_count) || 0,
                    has_prayed: parseInt(prayer.has_prayed) || 0
                }));
                setTestimonies(testimonies);
            }
        } catch (err) {
            console.error('Error loading testimonies:', err);
        } finally {
            setLoadingTestimonies(false);
        }
    };

    const handleSubmitPrayer = async (e) => {
        if (!user.user_id) {
            return;
        }

        e.preventDefault();
        
        if (prayerText.trim() === '') return;
        if (charCount > 1000) return;
        
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            
            const response = await fetch(`${backendUrl}/create_prayer.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    prayer_text: prayerText,
                    is_anonymous: isAnonymous,
                    group_id: groupId
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setPrayerText('');
                setIsAnonymous(false);
                setCharCount(0);
                setSubmitSuccess(true);
                setShowPrayerForm(false);
                
                // Refresh prayers list
                fetchGroupPrayers();
                fetchMyPrayers();
                fetchTestimonies();
                
                // Hide success message after 3 seconds
                setTimeout(() => {
                    setSubmitSuccess(false);
                }, 3000);
            } else {
                setSubmitError(data.message || 'Failed to submit prayer request');
            }
        } catch (err) {
            setSubmitError('Network error. Please try again later.');
            console.error('Error submitting prayer:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePray = async (prayerId) => {
        try {
            const response = await fetch(`${backendUrl}/pray.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prayer_id: prayerId,
                    user_id: user.user_id,
                    encouragement: "" // No encouragement message
                })
            });

            const data = await response.json();
            console.log('Pray API response:', data); // Debug logging

            if (data.success) {
                // Don't update the UI optimistically - always refresh from server
                // This ensures we get the accurate data from the database
                fetchGroupPrayers();
                fetchMyPrayers();
                fetchTestimonies();
                
                // Show success message
                showAlert('success', 'Success', 'Prayer recorded successfully!', 3000);
            } else {
                console.error('Pray API failed:', data.message);
                showAlert('error', 'Error', 'Failed to record prayer: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error praying for request:', err);
            showAlert('error', 'Network Error', 'Network error. Please try again.');
        }
    };

    // Handle opening encouragement modal
    const handleOpenEncouragementModal = (prayerId) => {
        setSelectedPrayerId(prayerId);
        setEncouragementText('');
        setShowEncouragementModal(true);
    };

    // Handle submitting encouragement
    const handleSubmitEncouragement = async () => {
        if (!user.user_id || !selectedPrayerId) {
            return;
        }

        if (encouragementText.trim() === '') return;
        
        try {
            setSubmittingEncouragement(true);
            
            const response = await fetch(`${backendUrl}/pray.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    prayer_id: selectedPrayerId,
                    encouragement: encouragementText
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Refresh the prayers list to get accurate counts from the server
                fetchGroupPrayers();
                fetchMyPrayers();
                fetchTestimonies();
                
                // Close modal and reset form
                setShowEncouragementModal(false);
                setSelectedPrayerId(null);
                setEncouragementText('');
                
                // Show success message
                showAlert('success', 'Success', 'Encouragement sent successfully!', 3000);
            } else {
                console.error('Encouragement API failed:', data.message);
                showAlert('error', 'Error', 'Failed to send encouragement: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error sending encouragement:', err);
            showAlert('error', 'Network Error', 'Network error. Please try again.');
        } finally {
            setSubmittingEncouragement(false);
        }
    };

    const handleSubmitTestimony = async (prayerId) => {
        if (!testimonyText.trim()) return;

        try {
            setSubmittingTestimony(true);

            const response = await fetch(`${backendUrl}/add_testimony.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prayer_id: prayerId,
                    user_id: user.user_id,
                    testimony: testimonyText
                })
            });

            const data = await response.json();

            if (data.success) {
                setTestimonyText('');
                setShowTestimonyForm(null);
                fetchGroupPrayers();
                fetchMyPrayers();
                fetchTestimonies();
            }
        } catch (err) {
            console.error('Error submitting testimony:', err);
        } finally {
            setSubmittingTestimony(false);
        }
    };

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            
            <div className="container-fluid p-3 flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <div>
                            <h2 className="mb-0">
                                <FontAwesomeIcon icon={faPray} className="me-2" />
                                Prayer Requests
                            </h2>
                            <small className="text-muted">Share and pray together</small>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                fetchGroupPrayers();
                                fetchMyPrayers();
                                fetchTestimonies();
                            }}
                            disabled={loadingPrayers}
                        >
                            <FontAwesomeIcon icon={faSpinner} className={`me-1 ${loadingPrayers ? 'fa-spin' : ''}`} />
                            Refresh
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowPrayerForm(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Add Prayer Request
                        </button>
                    </div>
                </div>

                {/* Success Message */}
                {submitSuccess && (
                    <Alert variant="success" className="mb-3">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Prayer request submitted successfully!
                    </Alert>
                )}

                {/* Error Message */}
                {loadError && (
                    <Alert variant="danger" className="mb-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {loadError}
                    </Alert>
                )}

                {/* Tabs */}
                <Tabs defaultActiveKey="prayers" className="mb-4">
                    <Tab eventKey="prayers" title={`Community Prayers (${groupPrayers.length})`}>
                        {loadingPrayers ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-3">Loading prayer requests...</div>
                            </div>
                        ) : groupPrayers.length === 0 ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faPray} size="3x" className="text-muted mb-3" />
                                <h5 className="text-muted">No prayer requests yet</h5>
                                <p className="text-muted">
                                    Be the first to share a prayer request with your group.
                                </p>
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => setShowPrayerForm(true)}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    Add Prayer Request
                                </button>
                            </div>
                        ) : (
                            <div>
                                {groupPrayers.map((prayer) => (
                                    <Card className="mb-3" key={prayer.id}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <Card.Subtitle className="text-muted mb-1 d-none" style={{ fontSize: '0.9rem' }}>
                                                        {prayer.is_anonymous ? 'Anonymous' : (prayer.user && prayer.user.name ? prayer.user.name : 'User')}
                                                    </Card.Subtitle>
                                                    <Card.Subtitle className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                                                        {timeAgo(prayer.created_at)}
                                                    </Card.Subtitle>
                                                </div>

                                                <div className="d-flex">
                                                    <Badge bg="warning" className="me-2">
                                                        {prayer.prayer_count || 0} Prayer{`${prayer.prayer_count > 1 ? 's' : ''}`}
                                                    </Badge>
                                                </div>
                                            </div>
                                            
                                            <Card.Text>{prayer.prayer_text}</Card.Text>
                                            
                                            {prayer.testimony && (
                                                <Alert variant="success" className="mt-3 mb-2">
                                                    <strong>Testimony:</strong> {prayer.testimony}
                                                </Alert>
                                            )}
                                            
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="d-flex">
                                                    <button 
                                                        className={`btn btn-sm ${prayer.user_prayed > 0 ? "btn-outline-primary" : "btn-primary"} me-2`}
                                                        onClick={() => handlePray(prayer.id)}
                                                        disabled={prayer.user_prayed > 0}
                                                    >
                                                        {prayer.user_prayed > 0 ? "You Prayed" : "Pray"}
                                                    </button>

                                                    {prayer.user_prayed === 0 && (
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => handleOpenEncouragementModal(prayer.id)}
                                                        >
                                                            Encourage
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Tab>

                    <Tab eventKey="my-prayers" title={`My Prayers (${myPrayers.length})`}>
                        {loadingMyPrayers ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-3">Loading your prayer requests...</div>
                            </div>
                        ) : myPrayers.length === 0 ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faPray} size="3x" className="text-muted mb-3" />
                                <h5 className="text-muted">No prayer requests yet</h5>
                                <p className="text-muted">
                                    Share your first prayer request with the group.
                                </p>
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => setShowPrayerForm(true)}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    Add Prayer Request
                                </button>
                            </div>
                        ) : (
                            <div>
                                {myPrayers.map((prayer) => (
                                    <Card className="mb-3 border-primary" key={prayer.id}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <Card.Subtitle className="text-muted mb-1 d-none" style={{ fontSize: '0.9rem' }}>
                                                        {prayer.is_anonymous ? 'Anonymous' : (prayer.user && prayer.user.name ? prayer.user.name : 'You')}
                                                    </Card.Subtitle>
                                                    <Card.Subtitle className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                                                        {timeAgo(prayer.created_at)}
                                                    </Card.Subtitle>
                                                </div>

                                                <div className="d-flex">
                                                    <Badge bg="warning" className="me-2">
                                                        {prayer.prayer_count || 0} Prayer{`${prayer.prayer_count > 1 ? 's' : ''}`}
                                                    </Badge>
                                                    {prayer.testimony && (
                                                        <Badge bg="success">
                                                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                            Answered
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <Card.Text>{prayer.prayer_text}</Card.Text>
                                            
                                            {prayer.testimony && (
                                                <Alert variant="success" className="mt-3 mb-2">
                                                    <strong>Testimony:</strong> {prayer.testimony}
                                                </Alert>
                                            )}
                                            
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="d-flex">
                                                    {!prayer.testimony && (
                                                        <button 
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => setShowTestimonyForm(prayer.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                            Share Testimony
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Tab>

                    <Tab eventKey="testimonies" title={`Testimonies (${testimonies.length})`}>
                        {loadingTestimonies ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-3">Loading testimonies...</div>
                            </div>
                        ) : testimonies.length === 0 ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-muted mb-3" />
                                <h5 className="text-muted">No testimonies yet</h5>
                                <p className="text-muted">
                                    Testimonies will appear here when prayers are answered.
                                </p>
                            </div>
                        ) : (
                            <div>
                                {testimonies.map((testimony) => (
                                    <Card className="mb-3 border-success" key={testimony.id}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <Card.Subtitle className="text-muted mb-1 d-none" style={{ fontSize: '0.9rem' }}>
                                                        {testimony.is_anonymous ? 'Anonymous' : (testimony.user && testimony.user.name ? testimony.user.name : 'User')}
                                                    </Card.Subtitle>
                                                    <Card.Subtitle className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                                                        {timeAgo(testimony.updated_at)}
                                                    </Card.Subtitle>
                                                </div>

                                                <div className="d-flex">
                                                    <Badge bg="success" className="me-2">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                        Answered
                                                    </Badge>
                                                    <Badge bg="warning" className="me-2">
                                                        {testimony.prayer_count || 0} Prayer{`${testimony.prayer_count > 1 ? 's' : ''}`}
                                                    </Badge>
                                                </div>
                                            </div>
                                            
                                            <Card.Text>{testimony.prayer_text}</Card.Text>
                                            
                                            <Alert variant="success" className="mt-3 mb-2">
                                                <strong>Testimony:</strong> {testimony.testimony}
                                            </Alert>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Tab>
                </Tabs>
            </div>

            <Footer />

            {/* Prayer Request Form Modal */}
            <Modal show={showPrayerForm} onHide={() => setShowPrayerForm(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Add Prayer Request
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitPrayer}>
                    <Modal.Body>
                        {submitError && (
                            <Alert variant="danger">
                                {submitError}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Prayer Request</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={prayerText}
                                onChange={(e) => setPrayerText(e.target.value)}
                                placeholder="Share your prayer request with the group..."
                                maxLength={1000}
                                required
                            />
                            <Form.Text className="text-muted">
                                {charCount}/1000 characters
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Submit anonymously"
                                checked={isAnonymous}
                                onChange={(e) => setIsAnonymous(e.target.checked)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button 
                            variant="secondary" 
                            onClick={() => setShowPrayerForm(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit"
                            disabled={isSubmitting || prayerText.trim() === '' || charCount > 1000}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Prayer Request'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Testimony Form Modal */}
            <Modal show={showTestimonyForm} onHide={() => setShowTestimonyForm(null)}>
                <Modal.Header closeButton>
                    <Modal.Title>Share Testimony</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>How was your prayer answered?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={testimonyText}
                            onChange={(e) => setTestimonyText(e.target.value)}
                            placeholder="Share how God answered your prayer..."
                            required
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowTestimonyForm(null)}
                        disabled={submittingTestimony}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={() => handleSubmitTestimony(showTestimonyForm)}
                        disabled={submittingTestimony || !testimonyText.trim()}
                    >
                        {submittingTestimony ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Submitting...
                            </>
                        ) : (
                            'Share Testimony'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Encouragement Modal */}
            <Modal show={showEncouragementModal} onHide={() => setShowEncouragementModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Send Words of Encouragement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Your Message of Encouragement</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={encouragementText}
                            onChange={(e) => setEncouragementText(e.target.value)}
                            placeholder="Share an encouraging message..."
                            maxLength={700}
                        />
                        <small className="text-muted d-block text-end mt-1">
                            {encouragementText.length}/700 characters
                        </small>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowEncouragementModal(false)}
                        disabled={submittingEncouragement}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmitEncouragement}
                        disabled={submittingEncouragement || encouragementText.trim() === ''}
                    >
                        {submittingEncouragement ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Sending...
                            </>
                        ) : (
                            'Send Encouragement'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Custom Alert Component */}
            <AlertComponent />
        </div>
    );
};

export default GroupPrayerRequests;